/**
 * Pure helpers for the email CLAIM QUEUE (§ B8, catch-all arm).
 *
 * Mail that arrives at a traveller's tokenised address (`farecheck+<token>@…`)
 * carries its own proof of ownership: the token is a bearer secret, so delivery
 * IS attribution. Mail that arrives at the catch-all carries nothing of the
 * kind. All we have is a `From:` header that happens to match a registered
 * account — and a `From:` header is a string the sender chose. It is a HINT,
 * never evidence.
 *
 * So the queue exists to move attribution from a header we cannot trust to a
 * human who can actually know: the traveller looks at the booking and says
 * "yes, mine" or "not mine". THE TRAVELLER IS THE SECURITY CONTROL HERE. Every
 * wording decision below follows from that one fact.
 *
 * Three rules the copy must never break:
 *
 *   1. NEVER present an unconfirmed sender as confirmed. `senderVerified` is
 *      believed only when it is exactly `true`; null, undefined, absent or any
 *      non-boolean is treated as unconfirmed. Fail closed — a wrong "verified"
 *      badge is the one error that converts a phishing email into a trusted
 *      itinerary write.
 *   2. NEVER accuse, and NEVER reassure. "Unconfirmed" is not "forged": the
 *      overwhelming majority of these are ordinary hotel confirmations whose
 *      domain simply publishes no usable DKIM/SPF. Calling them fraudulent
 *      trains travellers to ignore the warning, which is worse than no warning.
 *      But softening it to "probably fine" hands away the only control we have.
 *      The copy states the mechanism plainly and gives the traveller the test
 *      they can actually apply: do you recognise this booking?
 *   3. NEVER let a claim decision rest on untrusted text alone. The parsed
 *      summary exists so the traveller recognises a REAL booking of their own,
 *      not so they trust the email. Subject, sender and parsed values are all
 *      attacker-controlled: they are sanitised and truncated here, and rendered
 *      as text only — never as HTML — by the card.
 */

/** How long an unclaimed catch-all message stays claimable. */
export const CLAIM_WINDOW_DAYS = 30;

/** A claimable message, exactly as `GET /api/booking-email/claimable` returns it. */
export interface ClaimableMessage {
  id: string;
  subject: string | null;
  fromAddress: string | null;
  receivedAt: string | null;
  expiresAt: string | null;
  /** Whether the sending domain's own signature checked out. Believed only when `true`. */
  senderVerified: boolean | null;
  /** Whatever the parser managed to extract. Shape is NOT guaranteed. */
  parsed: unknown;
  statusDetail: string | null;
}

export type ClaimTone = 'good' | 'attention' | 'inert';

const MS_PER_DAY = 86_400_000;

/** Truncation budgets. Untrusted text must not be able to blow out a row. */
const MAX_SUBJECT = 120;
const MAX_FROM = 80;
const MAX_DETAIL = 160;
const MAX_PARSED_VALUE = 60;
const MAX_CONFIRMATION = 32;
const MAX_RAW_DATE = 24;

/**
 * Make an attacker-controlled string safe to lay out, and only to lay out.
 *
 * Three separate jobs, all of them load-bearing:
 *
 *   - Control characters and newlines are removed. A subject containing "\n"
 *     could otherwise push the buttons out of a row, or fake a second field.
 *   - Bidirectional overrides (U+202A–U+202E, U+2066–U+2069) are removed. These
 *     visually REVERSE text, which is a published trick for making
 *     `evil@attacker.com` render as something that reads like `hotel.com` —
 *     precisely the deception this whole card is meant to defeat.
 *   - The result is truncated with an ellipsis so no single value can break the
 *     layout regardless of length.
 *
 * This is NOT escaping and is not a substitute for it. Safety against injection
 * comes from React rendering these as text nodes; this function is about the
 * traveller reading what was actually sent.
 */
export function cleanText(value: unknown, max: number): string {
  if (typeof value !== 'string') return '';
  const stripped = value
    // eslint-disable-next-line no-control-regex
    .replace(/[\u0000-\u001F\u007F]/g, ' ')
    .replace(/[\u200B\u200E\u200F\u202A-\u202E\u2066-\u2069]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
  if (stripped.length <= max) return stripped;
  return `${stripped.slice(0, Math.max(0, max - 1)).trimEnd()}…`;
}

export interface SenderPresentation {
  /** The address as it should appear on screen. Never empty. */
  address: string;
  /** True ONLY when the backend positively confirmed the sending domain. */
  confirmed: boolean;
  /** Short badge text. */
  label: string;
  tone: ClaimTone;
  /**
   * The full warning, or null when the sender was confirmed. Present means the
   * card MUST render it — this is the sentence that makes the traveller the
   * control rather than a rubber stamp.
   */
  warning: string | null;
}

/**
 * How the sender of a claimable message should read.
 *
 * The unconfirmed wording is the single most consequential judgement in this
 * file, so the reasoning is written down rather than left in a commit message:
 *
 *   "We could not confirm this really came from …" — states OUR limitation, not
 *   the sender's guilt. True of a legitimate hotel with no DKIM and of a
 *   forgery alike, which is the point: we genuinely cannot tell them apart, and
 *   pretending otherwise in either direction is a lie.
 *
 *   "Anyone can put another address in an email's From line." — gives the
 *   mechanism in one plain sentence, no jargon. Without it "unconfirmed" reads
 *   as bureaucratic noise and gets clicked through.
 *
 *   "Only claim it if you recognise the booking." — the actionable test, and the
 *   handover of the decision. It is phrased as the condition for claiming, not
 *   as advice, because the traveller is the last check before this writes to
 *   their trip.
 *
 * Rejected: "Warning: possibly forged" (accuses the 90% that are ordinary
 * hotels, and desensitises), "Sender could not be verified — this is normal"
 * (reassures away the only control), and a bare "Unverified" chip (no mechanism,
 * no action, invisible in practice).
 */
export function presentSender(
  fromAddress: string | null | undefined,
  senderVerified: unknown
): SenderPresentation {
  const cleaned = cleanText(fromAddress, MAX_FROM);
  const address = cleaned || '(no sender address)';

  // Fail closed: anything that is not literally `true` is unconfirmed.
  if (senderVerified === true) {
    return {
      address,
      confirmed: true,
      label: 'Sender checks passed',
      tone: 'good',
      warning: null,
    };
  }

  return {
    address,
    confirmed: false,
    label: 'Sender not confirmed',
    tone: 'attention',
    warning:
      `We could not confirm this really came from ${address}. Anyone can put ` +
      `another address in an email's From line, so only claim it if you ` +
      `recognise the booking.`,
  };
}

export interface ExpiryPresentation {
  /** Whole days remaining, 0 when under a day or gone, null when unknown. */
  daysLeft: number | null;
  /** Human string. Never empty — "unknown" is stated, not hidden. */
  text: string;
  expired: boolean;
  /** Under three days, or already gone: worth colouring. */
  urgent: boolean;
}

/**
 * How much of the claim window is left.
 *
 * A missing or unparseable `expiresAt` reports UNKNOWN rather than silently
 * implying unlimited time. The traveller losing a booking to a window they were
 * never shown is the failure this avoids.
 */
export function describeExpiry(
  expiresAt: string | null | undefined,
  now: Date = new Date()
): ExpiryPresentation {
  if (!expiresAt || typeof expiresAt !== 'string') {
    return { daysLeft: null, text: 'Expiry unknown', expired: false, urgent: false };
  }
  const t = Date.parse(expiresAt);
  if (Number.isNaN(t)) {
    return { daysLeft: null, text: 'Expiry unknown', expired: false, urgent: false };
  }

  const diff = t - now.getTime();
  if (diff <= 0) {
    // Still shown, still honest: the row is dead, and saying so beats a silent
    // failure when Claim comes back rejected.
    return { daysLeft: 0, text: 'Claim window closed', expired: true, urgent: true };
  }
  if (diff < MS_PER_DAY) {
    return { daysLeft: 0, text: 'Less than a day left', expired: false, urgent: true };
  }

  const days = Math.floor(diff / MS_PER_DAY);
  return {
    daysLeft: days,
    text: `${days} day${days === 1 ? '' : 's'} left`,
    expired: false,
    urgent: days <= 3,
  };
}

/** When it arrived, in words. Null when we were not told. */
export function describeReceived(
  receivedAt: string | null | undefined,
  now: Date = new Date()
): string | null {
  if (!receivedAt || typeof receivedAt !== 'string') return null;
  const t = Date.parse(receivedAt);
  if (Number.isNaN(t)) return null;
  const mins = Math.floor((now.getTime() - t) / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} hour${hrs === 1 ? '' : 's'} ago`;
  const days = Math.floor(hrs / 24);
  return `${days} day${days === 1 ? '' : 's'} ago`;
}

/**
 * The card's one-line summary.
 *
 * Says "confirm they are yours" rather than "review": the traveller's answer is
 * the attribution, and the copy should not make it sound like a formality.
 */
export function summariseQueue(count: number): string {
  const n = Number.isFinite(count) && count > 0 ? Math.floor(count) : 0;
  if (n === 0) return 'Nothing is waiting to be claimed.';
  if (n === 1) {
    return '1 forwarded email arrived without proof of who sent it. Confirm it is yours and we will add it to your trips.';
  }
  return `${n} forwarded emails arrived without proof of who sent them. Confirm which are yours and we will add them to your trips.`;
}

/* ------------------------------------------------------------------------- *
 * Reading the `parsed` blob
 *
 * `parsed` is whatever the extractor produced for whatever the sender sent, so
 * it is treated as `unknown` throughout: it may be null, a string, an array, a
 * deeply nested object, or a shape a newer backend invented this morning. Every
 * read is guarded, every value sanitised. The goal is only RECOGNITION — enough
 * for the traveller to think "that's my hotel" — never completeness.
 * ------------------------------------------------------------------------- */

const NAME_KEYS = [
  'hotelName',
  'propertyName',
  'hotel',
  'property',
  'airline',
  'carrier',
  'name',
  'title',
];
const CONFIRMATION_KEYS = [
  'confirmationNumber',
  'confirmationCode',
  'confirmation',
  'recordLocator',
  'reservationNumber',
  'bookingReference',
  'pnr',
];
const START_KEYS = ['checkIn', 'checkInDate', 'startDate', 'departureDate', 'depart', 'start'];
const END_KEYS = ['checkOut', 'checkOutDate', 'endDate', 'returnDate', 'end'];

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function scalarText(value: unknown, max: number): string | null {
  if (typeof value === 'string') return cleanText(value, max) || null;
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return null;
}

/**
 * First usable value for any of `keys`, checking the top level and then one
 * level of nesting (a `{ booking: { hotelName } }` wrapper is common). Bounded
 * on purpose: no recursion into hostile, arbitrarily deep input.
 */
function pickValue(parsed: unknown, keys: string[], max: number): string | null {
  if (!isRecord(parsed)) return null;
  for (const key of keys) {
    const found = scalarText(parsed[key], max);
    if (found) return found;
  }
  for (const nested of Object.values(parsed)) {
    if (!isRecord(nested)) continue;
    for (const key of keys) {
      const found = scalarText(nested[key], max);
      if (found) return found;
    }
  }
  return null;
}

/**
 * A date as a traveller reads dates.
 *
 * UTC parts deliberately: a date-only "2026-03-12" must render as 12 Mar in
 * every timezone, not slip a day west of Greenwich. An unparseable value falls
 * back to the sanitised raw string — "March, sometime" from a real hotel email
 * still helps recognition, and it is safe because it is truncated text.
 */
export function formatDay(value: string | null): string | null {
  if (!value) return null;
  const t = Date.parse(value);
  if (Number.isNaN(t)) return cleanText(value, MAX_RAW_DATE) || null;
  const d = new Date(t);
  return `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
}

/**
 * What we think this booking is, in one line, or null when we got nothing.
 *
 * Null is a legitimate and common answer, and the card says so out loud rather
 * than showing a blank space: "we could not read the details" is exactly the
 * situation in which a traveller should be more careful about claiming, not
 * less.
 */
export function describeParsed(parsed: unknown): string | null {
  const name = pickValue(parsed, NAME_KEYS, MAX_PARSED_VALUE);
  const confirmation = pickValue(parsed, CONFIRMATION_KEYS, MAX_CONFIRMATION);
  const start = formatDay(pickValue(parsed, START_KEYS, MAX_RAW_DATE));
  const end = formatDay(pickValue(parsed, END_KEYS, MAX_RAW_DATE));

  const parts: string[] = [];
  if (name) parts.push(name);
  if (start && end) parts.push(`${start} – ${end}`);
  else if (start) parts.push(`from ${start}`);
  else if (end) parts.push(`until ${end}`);
  if (confirmation) parts.push(`conf. ${confirmation}`);

  if (parts.length === 0) return null;
  return parts.join(' · ');
}

/** Everything one row needs, all of it already safe to lay out. */
export interface ClaimableRow {
  id: string;
  subject: string;
  sender: SenderPresentation;
  /** One-line booking description, or null when nothing was parsed. */
  parsedSummary: string | null;
  /** Copy for the null case — the card renders this instead of a blank. */
  parsedFallback: string;
  expiry: ExpiryPresentation;
  received: string | null;
  detail: string | null;
  /** True when claiming is pointless because the window is gone. */
  actionable: boolean;
}

export function presentClaimable(
  message: ClaimableMessage,
  now: Date = new Date()
): ClaimableRow {
  const expiry = describeExpiry(message?.expiresAt, now);
  return {
    id: typeof message?.id === 'string' ? message.id : String(message?.id ?? ''),
    subject: cleanText(message?.subject, MAX_SUBJECT) || '(no subject)',
    sender: presentSender(message?.fromAddress, message?.senderVerified),
    parsedSummary: describeParsed(message?.parsed),
    parsedFallback: 'We could not read any booking details out of this email.',
    expiry,
    received: describeReceived(message?.receivedAt, now),
    detail: cleanText(message?.statusDetail, MAX_DETAIL) || null,
    actionable: !expiry.expired,
  };
}

export function presentClaimQueue(
  messages: ClaimableMessage[] | null | undefined,
  now: Date = new Date()
): ClaimableRow[] {
  if (!Array.isArray(messages)) return [];
  return messages
    .filter((m): m is ClaimableMessage => isRecord(m))
    .map((m) => presentClaimable(m, now));
}
